const baseUrl = import.meta.env.VITE_IDENTITY_SERVICE_URL;

if (!baseUrl) {
    throw new Error("VITE_IDENTITY_SERVICE_URL is required.");
}

export const identityServiceUrl = baseUrl;

export interface SessionResponse {
    isAuthenticated: boolean;
    userId: string | null;
    email: string | null;
    displayName: string | null;
    tenantId: string | null;
    roles: string[];
}

export interface SignUpPayload {
    tenantName: string;
    subdomain: string;
    mailDomain: string;
    email: string;
    displayName: string;
    password: string;
}

export interface LoginPayload {
    email: string;
    password: string;
}

/** Second factors a user can finish a sign-in with (Mobile Phase 1 contract §2). */
export type TwoFactorMethod = "push" | "totp" | "recovery";

// Login can succeed outright, or demand a second step.
export interface LoginResponse {
    requiresTwoFactor: boolean;
    session: SessionResponse | null;
    /** Domain requires MFA and the user only has a passkey — must use it. */
    mustUsePasskey?: boolean;
    /** Domain requires MFA and the user has no factor yet — must set one up. */
    mfaSetupRequired?: boolean;
    /** When requiresTwoFactor: which second factors this account can use. */
    twoFactorMethods?: TwoFactorMethod[] | null;
}

/** A sign-in request waiting for approval in Athavita Authenticator. */
export interface ApprovalStart {
    challengeId: string;
    /** Two digits the user taps on the phone. */
    number: string;
    expiresAt: string;
    /** Push sign-in only: how many phones were notified. */
    deviceCount?: number;
}

export type ApprovalStatus = "pending" | "approved" | "denied" | "expired";

export interface ApprovalStatusResponse {
    status: ApprovalStatus;
    /** Present when approved — the sign-in is complete (cookie set). */
    session?: SessionResponse;
}

/** An Athavita Authenticator install that can approve sign-ins. */
export interface AuthenticatorDto {
    deviceId: string;
    name: string | null;
    model: string | null;
    platform: string;
    enrolledAt: string | null;
    lastUsedAt: string | null;
    passwordless: boolean;
}

export interface TwoFactorLoginPayload {
    code: string;
    isRecoveryCode?: boolean;
}

export interface MfaStatusResponse {
    enabled: boolean;
    hasAuthenticator: boolean;
}

export interface MfaEnrollResponse {
    sharedKey: string;
    authenticatorUri: string;
}

export interface MfaPolicyResponse {
    allowAuthenticator: boolean;
    allowBiometric: boolean;
    requireMfa?: boolean;
}

export interface MfaRecoveryCodesResponse {
    recoveryCodes: string[];
}

export interface PasskeyDto {
    id: string;
    deviceName: string;
    createdAt: string;
    lastUsedAt: string | null;
}

export interface ChangePasswordPayload {
    oldPassword: string;
    newPassword: string;
}

export interface UpdateProfilePayload {
    displayName: string;
}

/** A tenant's enabled SAML provider, for the email-first SSO discovery step on the sign-in page. */
export interface SamlLoginProvider {
    id: string;
    provider: string;
    buttonLabel: string;
}

// Identity-service returns RFC 7807 problem details on 400/409; bubble the
// title + detail up so forms can render them directly.
export class IdentityApiError extends Error {
    constructor(
        message: string,
        public readonly status: number,
        public readonly detail?: string,
        /** Machine-readable `error` from `{ error: "…" }` bodies (e.g. "no_authenticator", "rate_limited"). */
        public readonly code?: string
    ) {
        super(message);
        this.name = "IdentityApiError";
    }
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
    return requestJson<T>("POST", path, body);
}

async function requestJson<T>(method: string, path: string, body: unknown): Promise<T> {
    // GET/HEAD cannot carry a body (fetch throws), and a null body is meaningless.
    const hasBody = body !== null && method !== "GET" && method !== "HEAD";
    const response = await fetch(new URL(path, baseUrl).toString(), {
        method,
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        ...(hasBody ? { body: JSON.stringify(body) } : {}),
        credentials: "include"
    });

    if (response.ok) {
        // POST /api/auth/logout returns an empty 200; everything else returns JSON.
        const text = await response.text();
        return (text ? JSON.parse(text) : null) as T;
    }

    if (response.status === 401) {
        throw new IdentityApiError("Invalid email or password.", 401);
    }

    let title = response.statusText;
    let detail: string | undefined;
    let code: string | undefined;
    try {
        const body = (await response.json()) as {
            title?: string;
            detail?: string;
            errors?: Record<string, string[]>;
            error?: string;
            error_description?: string;
        };
        title = body.title ?? title;
        detail = body.detail
            ?? body.error_description
            ?? (body.errors ? Object.values(body.errors).flat().join(" ") : undefined);
        code = typeof body.error === "string" ? body.error : undefined;
    } catch {
        // non-JSON response — stick with statusText
    }

    throw new IdentityApiError(title, response.status, detail, code);
}

export const identityApi = {
    signup: (payload: SignUpPayload) => postJson<SessionResponse>("/api/auth/signup", payload),
    login: (payload: LoginPayload) => postJson<LoginResponse>("/api/auth/login", payload),
    loginTwoFactor: (payload: TwoFactorLoginPayload) =>
        postJson<LoginResponse>("/api/auth/login/2fa", payload),
    logout: () => postJson<null>("/api/auth/logout", {}),

    // Athavita Authenticator sign-in approval (Mobile Phase 1). The start calls set the HttpOnly
    // `athavita.pa` cookie that ties the request to this browser; only it can poll.
    pushStart: (returnUrl?: string) =>
        postJson<ApprovalStart>("/api/auth/login/push/start", { returnUrl: returnUrl ?? null }),
    pushStatus: (challengeId: string) =>
        requestJson<ApprovalStatusResponse>("GET", `/api/auth/login/push/${encodeURIComponent(challengeId)}`, null),
    passwordlessStart: (email: string, returnUrl?: string) =>
        postJson<ApprovalStart>("/api/auth/login/passwordless/start", { email, returnUrl: returnUrl ?? null }),
    passwordlessStatus: (challengeId: string) =>
        requestJson<ApprovalStatusResponse>("GET", `/api/auth/login/passwordless/${encodeURIComponent(challengeId)}`, null),
    authenticators: () => requestJson<AuthenticatorDto[]>("GET", "/api/auth/authenticators", null),
    setAuthenticatorPasswordless: (deviceId: string, passwordless: boolean) =>
        requestJson<null>("PUT", `/api/auth/authenticators/${encodeURIComponent(deviceId)}`, { passwordless }),
    removeAuthenticator: (deviceId: string) =>
        requestJson<null>("DELETE", `/api/auth/authenticators/${encodeURIComponent(deviceId)}`, null),

    // MFA self-service (cookie-authenticated)
    mfaPolicy: () => requestJson<MfaPolicyResponse>("GET", "/api/auth/mfa-policy", null),
    mfaStatus: () => requestJson<MfaStatusResponse>("GET", "/api/auth/mfa", null),
    mfaEnroll: () => postJson<MfaEnrollResponse>("/api/auth/mfa/enroll", {}),
    mfaEnable: (code: string) => postJson<MfaRecoveryCodesResponse>("/api/auth/mfa/enable", { code }),
    mfaDisable: () => postJson<null>("/api/auth/mfa/disable", {}),
    mfaRegenerateRecoveryCodes: () =>
        postJson<MfaRecoveryCodesResponse>("/api/auth/mfa/recovery-codes", {}),

    // Passkeys (WebAuthn). begin endpoints return raw fido2 options JSON.
    passkeyList: () => requestJson<PasskeyDto[]>("GET", "/api/auth/passkey", null),
    passkeyDelete: (id: string) =>
        requestJson<null>("DELETE", `/api/auth/passkey/${encodeURIComponent(id)}`, null),
    passkeyRegisterBegin: () => postJson<Record<string, unknown>>("/api/auth/passkey/register/begin", {}),
    passkeyRegisterFinish: (raw: unknown) => postJson<PasskeyDto>("/api/auth/passkey/register/finish", raw),
    passkeyLoginBegin: () => postJson<Record<string, unknown>>("/api/auth/passkey/login/begin", {}),
    passkeyLoginFinish: (raw: unknown) => postJson<LoginResponse>("/api/auth/passkey/login/finish", raw),
    changePassword: (payload: ChangePasswordPayload) =>
        postJson<null>("/api/auth/change-password", payload),
    updateProfile: (payload: UpdateProfilePayload) =>
        requestJson<null>("PUT", "/api/auth/profile", payload),
    // Email-first SSO discovery — resolves the email's domain to a tenant and returns its
    // enabled SAML providers. Unauthenticated (this IS the pre-login discovery step).
    samlProviders: (email: string) =>
        requestJson<SamlLoginProvider[]>("GET", `/api/saml/providers?email=${encodeURIComponent(email)}`, null),
    session: async (): Promise<SessionResponse> => {
        const response = await fetch(new URL("/api/auth/session", baseUrl).toString(), {
            credentials: "include",
            headers: { Accept: "application/json" }
        });
        if (!response.ok) {
            throw new IdentityApiError(response.statusText, response.status);
        }
        return (await response.json()) as SessionResponse;
    }
};
